import { AppContext } from 'Api/AppContext';
import { File, FileType } from 'Api/Dto/Drive';
import { HttpClient } from 'Api/HttpClient';
import { DriveService } from 'Api/Services';
import { IDiaporamaDialogContext } from 'App/Areas/Shares/Dialogs/IDiaporamaDialogContext';
import { getImageUrl, getPanoramaMobileUrl, getPdfUrl } from 'App/Helpers/AssetHelper';
import { injectTypes } from 'App/injectTypes';
import { lazyInject } from 'App/Inversify.config';
import { BaseDialogBehavior } from 'App/Services/DialogService/BaseDialogBehavior';
import { IVisonUriService } from 'App/Services/UriServices/Core';
import { ConfigurationBuilder } from 'Framework/Components/Controls/XView/Core';
import { Component, Watch } from 'vue-property-decorator';

interface IFile extends File {
    attributes?: Record<string, any>
}

type ContentTemplateType = {
    props: (dialog: VoDiaporamaDialog, file: IFile) => any,
    canRender: (file: IFile) => boolean,
    template: () => Promise<any>,
    icon?: string,
}

type ContentTemplateSelector = {
    fileType: FileType,
    templates: ContentTemplateType[],
}

type AvailableComponent =
    | 'XViewFlat'
    | 'XView360'
    | 'XMediaPlayer'
    | 'XOfficeViewer'
    | 'VoPdfViewer'

@Component({
    name: 'VoDiaporamaDialog'
})
export default class VoDiaporamaDialog extends BaseDialogBehavior<IDiaporamaDialogContext> {
    public created(): void {
        window.addEventListener('keyup', this._onKeyUp);
    }

    public beforeDestroy() {
        window.removeEventListener('keyup', this._onKeyUp);
    }

    private _onKeyUp(e: KeyboardEvent) {
        if (e.code == 'ArrowRight') {
            this.goToNextItem();
        }
        else if (e.code == 'ArrowLeft') {
            this.goToPreviousItem();
        }
    }

    public goToNextItem(): void {
        this.selectedIndex = this.nextItemIndex;
    }

    public goToPreviousItem(): void {
        this.selectedIndex = this.previousItemIndex;
    }

    protected _onDataContextChanged(newValue: IDiaporamaDialogContext, oldValue: IDiaporamaDialogContext): void {
        this.selectedIndex = newValue.itemsSource.indexOf(newValue.selectedItem);
    }

    public get nextItemIndex(): number {
        const nextIndex = this.selectedIndex + 1;
        const nbItems = this.dataContext.itemsSource.length;

        return nextIndex == nbItems
            ? 0
            : nextIndex;
    }

    public get previousItemIndex(): number {
        const previousIndex = this.selectedIndex - 1;
        const lastIndex = this.dataContext.itemsSource.length - 1;

        return previousIndex < 0
            ? lastIndex
            : previousIndex;
    }

    public get selectedItem(): IFile {
        const selectedFile = this.dataContext?.itemsSource[this.selectedIndex];

        return selectedFile?.isSymlink
            ? selectedFile.target
            : selectedFile;
    }

    public get displayName(): string {
        const extension = this.selectedItem.extension ?? '';

        return `${this.selectedItem.nameWithoutExtension}${extension}`;
    }

    @Watch('selectedIndex', { immediate: true })
    private _onSelectedIndexChanged(_newValue: number): void {
        const select = (): void => {
            //force refresh UI
            this.selectedTemplate = null;

            this.selectedTemplate = this.contentTemplates
                .find(c => c.fileType == this.selectedItem.fileType);

            this.isLoading = false;
        }

        this.isLoading = true;

        if (!this.selectedItem.attributes) {
            const _ = this._driveService
                .getFileInfo(this.selectedItem, this.dataContext.token)
                .then(fileInfo => {
                    this.selectedItem.attributes = fileInfo.attributes ?? {};

                    select();
                });
        }
        else {
            select();
        }
    }

    public readonly baseTranslationPath: string = 'Areas.Shared.VoDiaporamaDialog';

    public selectedTemplate: ContentTemplateSelector = null;
    public selectedIndex: number = 0;
    public selectedTemplateIndex: number = 0;
    public isLoading: boolean = false;

    public static readonly contentTemplateTypes: Record<AvailableComponent, ContentTemplateType> = {
        'XViewFlat': {
            props: (dialog: VoDiaporamaDialog, file: IFile) => ({
                configuration: ConfigurationBuilder.CreateFlatConfigurationWithImage(
                    dialog.dataContext?.token
                        ? dialog._uriService.getAbsoluteUri(getImageUrl(dialog._appContext.codeCulture, file.guid, 'optimized'), { 'token': dialog.dataContext.token })
                        : dialog._uriService.getAbsoluteUri(getImageUrl(dialog._appContext.codeCulture, file.guid, 'optimized')),
                    Number.parseInt(file.attributes?.width),
                    Number.parseInt(file.attributes?.height)
                )
            }),
            template: () => import('Framework/Components/Controls/XView/XView.vue'),
            canRender: (file: IFile): boolean =>
                Number.parseInt(file.attributes?.width) > 0
                    && Number.parseInt(file.attributes?.height) > 0,
            icon: 'fa-light fa-panorama',
        },
        'XView360': {
            props: (dialog: VoDiaporamaDialog, file: IFile) => ({
                configuration: ConfigurationBuilder.CreatePanoramaConfigurationWithComputedTile(
                    dialog.dataContext?.token
                        ? dialog._uriService.getAbsoluteUri(getPanoramaMobileUrl(dialog._appContext.codeCulture, file.guid), { 'token': dialog.dataContext.token })
                        : dialog._uriService.getAbsoluteUri(getPanoramaMobileUrl(dialog._appContext.codeCulture, file.guid)),
                    Math.min(Number.parseInt(file.attributes?.panoramaLevel), 3)
                )
            }),
            template: () => import('Framework/Components/Controls/XView/XView.vue'),
            canRender: (file: IFile): boolean =>
                Number.parseInt(file.attributes?.ratio) == 2
                    && Number.parseInt(file.attributes?.panoramaLevel) > 0,
            icon: 'fa-light fa-360-degrees',
        },
        'XMediaPlayer': {
            props: (dialog: VoDiaporamaDialog, file: IFile) => ({
                source: dialog._uriService.getAbsoluteUri(file.links.video, { 'token': dialog.dataContext?.token }),
                preview: dialog._uriService.getAbsoluteUri(file.links.preview, { 'token': dialog.dataContext?.token }),
                showControls: true,
                muted: false
            }),
            template: () => import('Framework/Components/Controls/XMediaPlayer.vue'),
            canRender: (_file: IFile): boolean => true,
        },
        'XOfficeViewer': {
            props: (dialog: VoDiaporamaDialog, file: IFile) => ({
                url: dialog._uriService.getAbsoluteUri(
                    file.links.download,
                    { 'token': dialog.dataContext?.token })
            }),
            template: () => import('Framework/Components/Controls/XOfficeViewer.vue'),
            canRender: (_file: IFile): boolean => true,
        },
        'VoPdfViewer': {
            props: (dialog: VoDiaporamaDialog, file: IFile) => {
                if (dialog._appContext.isStandalone) {
                    return {
                        pdfLoader: async () => await (await dialog._httpClient.getAsync(`asset/pdf/${file.guid}`, null, null, new Headers({
                            'Accept': 'application/octet-stream',
                            'Content-Type': 'application/octet-stream'
                        }))).arrayBuffer(),
                        fileName: `${file.nameWithoutExtension}${file.extension}`
                    }
                }

                return {
                    url: dialog._uriService.getAbsoluteUri(getPdfUrl(dialog._appContext.codeCulture, file.guid), { 'token': dialog.dataContext?.token }),
                    fileName: `${file.nameWithoutExtension}${file.extension}`
                }
            },
            template: () => import('App/Areas/Shares/VoPdfViewer.vue'),
            canRender: (_file: IFile): boolean => true,
        }
    };

    public readonly contentTemplates: ContentTemplateSelector[] = [
        {
            fileType: FileType.Image,
            templates: [
                VoDiaporamaDialog.contentTemplateTypes.XViewFlat,
                VoDiaporamaDialog.contentTemplateTypes.XView360
            ]
        },
        {
            fileType: FileType.PointCloud,
            templates: [
                VoDiaporamaDialog.contentTemplateTypes.XViewFlat,
                VoDiaporamaDialog.contentTemplateTypes.XView360
            ]
        },
        {
            fileType: FileType.Video,
            templates: [
                VoDiaporamaDialog.contentTemplateTypes.XMediaPlayer
            ]
        },
        {
            fileType: FileType.Office,
            templates: [
                VoDiaporamaDialog.contentTemplateTypes.XOfficeViewer
            ]
        },
        {
            fileType: FileType.Pdf,
            templates: [
                VoDiaporamaDialog.contentTemplateTypes.VoPdfViewer
            ]
        }
    ];

    @lazyInject(AppContext)
    private readonly _appContext: AppContext;

    @lazyInject(injectTypes.IVisonUriService)
    private readonly _uriService: IVisonUriService;

    @lazyInject(HttpClient)
    private readonly _httpClient: HttpClient;

    @lazyInject(DriveService)
    private readonly _driveService: DriveService;
}
